// Roles & Permissions admin panel (RBAC Phase 1 Step 7).
//
// Mounted as a sub-tab under Administration > Users & Security. Relies on
// backend endpoints live since commit c63e449:
//   GET    /api/workflows/roles
//   POST   /api/workflows/roles
//   PUT    /api/workflows/roles/:id
//   DELETE /api/workflows/roles/:id
//   PUT    /api/workflows/roles/:id/permissions
//   GET    /api/settings/permissions/taxonomy
//
// The per-role permission panel is the primary UX (Option A). Operators
// edit one role at a time; the grouped view is the default; Advanced view
// toggles to a flat list with the permission key shown in monospace.

import { useState, useEffect, useMemo, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { Btn, Card, Modal } from "./primitives";

type Role = {
  id: string;
  slug: string;
  display_name: string;
  description?: string | null;
  is_system: boolean;
  is_active: boolean;
  dashboard_variant?: string | null;
  sort_order?: number;
  user_count?: number;
  permissions?: Array<{ permission: string; granted: boolean }>;
};

type Permission = {
  key: string;
  label: string;
  description: string;
  group: string;
  defaultRoles: string[];
};

type FeatureGroup = { key: string; label: string; description: string };

const DASHBOARD_VARIANTS = ["DashboardView", "LawyerWorkbench", "RequestorDashboard", "ExecutiveDashboard"];

// slug helper — mirrors what the backend expects (lowercase, underscores,
// strip anything not alphanumeric/underscore/hyphen).
function slugify(s: string): string {
  return String(s || "").toLowerCase().trim()
    .replace(/[^a-z0-9_\- ]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export const RolesPermissionsPanel = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [taxonomyGroups, setTaxonomyGroups] = useState<FeatureGroup[]>([]);
  const [taxonomyPerms, setTaxonomyPerms] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [advancedView, setAdvancedView] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  // dirty grant state for the currently edited role:
  // { [permission.key]: boolean }  (true = granted)
  const [dirtyGrants, setDirtyGrants] = useState<Record<string, boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // Create/edit modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [rolesRes, taxRes] = await Promise.all([
      apiCall("/workflows/roles"),
      apiCall("/settings/permissions/taxonomy"),
    ]);
    if (rolesRes?.roles) setRoles(rolesRes.roles);
    if (taxRes?.groups) setTaxonomyGroups(taxRes.groups);
    if (taxRes?.permissions) setTaxonomyPerms(taxRes.permissions);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Permissions of modules switched off on this instance are hidden from the
  // editor, so an administrator is not offered grants for screens that do not
  // exist here.
  //
  // THIS IS A DISPLAY FILTER AND NOTHING MORE. `taxonomyPerms` is deliberately
  // left intact, because savePermissions() builds its PUT payload from that same
  // array — filtering it would omit the hidden permissions from every save and
  // the stored grants would be silently revoked. The requirement is that grants
  // are byte-identical before and after, so the filtered list is used ONLY where
  // rows are rendered.
  //
  // A permission belongs to a module when its key is NAMESPACED to that module
  // ("docint.read", "nav.docint"), not when it merely sits in that module's
  // group. That distinction is load-bearing: `versions.promote_to_company_draft`
  // is filed under group 'drafting' (config/permissions.js:386) but it drives
  // "Promote to company draft" and "Finalize Draft" on every contract request
  // (RequestDetail.tsx:1856-1857). Hiding the drafting GROUP wholesale would
  // have taken a core Requests control with it. Namespacing keeps it visible.
  const { disabledModules } = useClientScope();
  const hiddenModules = useMemo(
    () => (disabledModules || []).map((m) => String(m).toLowerCase()),
    [disabledModules]
  );
  const visibleTaxonomyPerms = useMemo(
    () => taxonomyPerms.filter((perm) => !hiddenModules.some(
      (mod) => perm.key === `nav.${mod}` || perm.key.startsWith(`${mod}.`)
    )),
    [taxonomyPerms, hiddenModules]
  );

  const selectedRole = useMemo(() => roles.find(r => r.id === selectedRoleId) || null, [roles, selectedRoleId]);

  // Current effective grant state for the selected role:
  // dirtyGrants if present, otherwise the role's persisted grants.
  const currentGrants = useMemo((): Record<string, boolean> => {
    if (!selectedRole) return {};
    const base: Record<string, boolean> = {};
    for (const p of selectedRole.permissions || []) {
      if (p && typeof p.permission === "string") base[p.permission] = p.granted !== false;
    }
    if (dirtyGrants) return { ...base, ...dirtyGrants };
    return base;
  }, [selectedRole, dirtyGrants]);

  const isDirty = !!dirtyGrants && Object.keys(dirtyGrants).length > 0;

  const openRole = (role: Role) => {
    setSelectedRoleId(role.id);
    setDirtyGrants(null);
    setBanner(null);
    setAdvancedView(false);
  };

  const closeRole = () => {
    setSelectedRoleId(null);
    setDirtyGrants(null);
    setBanner(null);
  };

  const toggleGrant = (permKey: string) => {
    if (!selectedRole) return;
    const currentValue = currentGrants[permKey] === true;
    setDirtyGrants(prev => ({ ...(prev || {}), [permKey]: !currentValue }));
  };

  const resetToDefaults = () => {
    if (!selectedRole) return;
    const patch: Record<string, boolean> = {};
    for (const perm of taxonomyPerms) {
      const shouldHave = perm.defaultRoles.includes(selectedRole.slug);
      const currentlyHas = currentGrants[perm.key] === true;
      if (shouldHave !== currentlyHas) patch[perm.key] = shouldHave;
    }
    if (Object.keys(patch).length === 0) {
      setBanner({ kind: "ok", text: "Already at defaults for this role." });
      return;
    }
    setDirtyGrants(prev => ({ ...(prev || {}), ...patch }));
    setBanner({ kind: "ok", text: `Staged ${Object.keys(patch).length} change(s) to restore defaults. Click Save to persist.` });
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    setBanner(null);
    const permissions = taxonomyPerms.map(p => ({
      permission: p.key,
      granted: currentGrants[p.key] === true,
    }));
    try {
      const r = await apiCall(`/workflows/roles/${selectedRole.id}/permissions`, {
        method: "PUT",
        body: JSON.stringify({ permissions }),
      });
      if (r && Array.isArray(r.permissions)) {
        setBanner({ kind: "ok", text: "Permissions saved." });
        setDirtyGrants(null);
        await load();
      } else {
        setBanner({ kind: "err", text: (r && r.error) || "Save failed" });
      }
    } catch (e: any) {
      setBanner({ kind: "err", text: e?.message || "Save failed" });
    }
    setSaving(false);
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  if (loading) return <div style={{ padding: 20, color: T.txD }}>Loading roles...</div>;

  return (
    <div>
      {/* Role list */}
      {!selectedRole && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Roles & Permissions</div>
              <div style={{ fontSize: 11, color: T.txD, marginTop: 2 }}>{roles.length} roles — {roles.filter(r => r.is_system).length} system, {roles.filter(r => !r.is_system).length} custom</div>
            </div>
            <Btn v="primary" sz="sm" onClick={() => setShowCreate(true)}>+ Create Role</Btn>
          </div>
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.brd}` }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Display Name</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Slug</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Type</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Users</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Dashboard</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Permissions</th>
                  <th style={{ textAlign: "right", padding: "10px 12px", fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map(r => {
                  const permCount = (r.permissions || []).filter(p => p && p.granted !== false).length;
                  const userCount = typeof r.user_count === "number" ? r.user_count : undefined;
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                      <td style={{ padding: "10px 12px", color: T.tx, fontWeight: 600 }}>{r.display_name}</td>
                      <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 11, color: T.txM }}>{r.slug}</td>
                      <td style={{ padding: "10px 12px" }}>
                        {r.is_system
                          ? <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".05em", padding: "2px 6px", borderRadius: 3, background: "#1d3a5c", color: "#9bd0ff" }}>SYSTEM</span>
                          : <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".05em", padding: "2px 6px", borderRadius: 3, background: "#2a2310", color: "#d6a35c" }}>CUSTOM</span>}
                      </td>
                      <td style={{ padding: "10px 12px", color: T.txM }}>{userCount !== undefined ? userCount : "\u2014"}</td>
                      <td style={{ padding: "10px 12px", color: T.txM, fontFamily: "monospace", fontSize: 11 }}>{r.dashboard_variant || "\u2014"}</td>
                      <td style={{ padding: "10px 12px", color: T.txM }}>{permCount}</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <Btn v="ghost" sz="sm" onClick={() => openRole(r)}>Permissions</Btn>
                        <Btn v="ghost" sz="sm" onClick={() => setEditRole(r)}>Edit</Btn>
                        {!r.is_system && (
                          <Btn v="ghost" sz="sm" onClick={() => setDeleteRole(r)} style={{ color: T.red }}>Delete</Btn>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* Per-role permission panel */}
      {selectedRole && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <Btn v="ghost" sz="sm" onClick={closeRole}>\u2190 Back to roles</Btn>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginTop: 6 }}>
                {selectedRole.display_name}
                <span style={{ fontSize: 10, fontFamily: "monospace", color: T.txD, marginLeft: 8 }}>{selectedRole.slug}</span>
                {selectedRole.is_system && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".05em", padding: "2px 6px", borderRadius: 3, background: "#1d3a5c", color: "#9bd0ff", marginLeft: 8 }}>SYSTEM</span>}
              </div>
              {selectedRole.description && <div style={{ fontSize: 11, color: T.txD, marginTop: 2 }}>{selectedRole.description}</div>}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <label style={{ fontSize: 11, color: T.txM, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <input type="checkbox" checked={advancedView} onChange={(e) => setAdvancedView(e.target.checked)} />
                Advanced view
              </label>
              <Btn v="ghost" sz="sm" onClick={resetToDefaults}>Reset to defaults</Btn>
              <Btn v="primary" sz="sm" onClick={savePermissions} disabled={!isDirty || saving}>
                {saving ? "Saving\u2026" : isDirty ? `Save (${Object.keys(dirtyGrants || {}).length})` : "Save"}
              </Btn>
            </div>
          </div>

          {banner && (
            <div style={{
              padding: "8px 12px",
              background: banner.kind === "ok" ? "#12301c" : "#3a1818",
              border: `1px solid ${banner.kind === "ok" ? "#2d7748" : "#803030"}`,
              color: banner.kind === "ok" ? "#8fe0a8" : "#ffaaaa",
              fontSize: 12, borderRadius: 4, marginBottom: 12,
            }}>
              {banner.text}
            </div>
          )}

          {advancedView ? (
            <Card style={{ padding: 0 }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: `1px solid ${T.brd}` }}>
                  <th style={{ width: 40, textAlign: "center", padding: "8px 8px", fontSize: 10, color: T.txD }}>Grant</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, color: T.txD }}>Permission</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, color: T.txD }}>Group</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, color: T.txD }}>Description</th>
                </tr></thead>
                <tbody>
                  {visibleTaxonomyPerms.map(p => {
                    const granted = currentGrants[p.key] === true;
                    const dirty = !!dirtyGrants && Object.prototype.hasOwnProperty.call(dirtyGrants, p.key);
                    return (
                      <tr key={p.key} style={{ borderBottom: `1px solid ${T.brd}` }}>
                        <td style={{ textAlign: "center", padding: "6px 8px" }}>
                          <input type="checkbox" checked={granted} onChange={() => toggleGrant(p.key)} />
                        </td>
                        <td style={{ padding: "6px 12px" }}>
                          <div style={{ color: T.tx, fontSize: 12 }}>{p.label}{dirty && <span style={{ color: "#d6a35c", marginLeft: 6 }}>*</span>}</div>
                          <div style={{ fontFamily: "monospace", fontSize: 10, color: T.txD }}>{p.key}</div>
                        </td>
                        <td style={{ padding: "6px 12px", color: T.txM, fontFamily: "monospace", fontSize: 10 }}>{p.group}</td>
                        <td style={{ padding: "6px 12px", color: T.txM, fontSize: 11 }}>{p.description}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {taxonomyGroups.map(g => {
                const groupPerms = visibleTaxonomyPerms.filter(p => p.group === g.key);
                if (groupPerms.length === 0) return null;
                const grantedCount = groupPerms.filter(p => currentGrants[p.key] === true).length;
                const isCollapsed = !!collapsedGroups[g.key];
                return (
                  <Card key={g.key} style={{ padding: 0, overflow: "hidden" }}>
                    <div onClick={() => toggleGroup(g.key)} style={{ padding: "10px 14px", borderBottom: isCollapsed ? "none" : `1px solid ${T.brd}`, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: T.bg2 }}>
                      <div>
                        <span style={{ fontSize: 12, color: T.txD, marginRight: 8 }}>{isCollapsed ? "\u25B8" : "\u25BE"}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{g.label}</span>
                        <span style={{ fontSize: 11, color: T.txD, marginLeft: 10 }}>{g.description}</span>
                      </div>
                      <span style={{ fontSize: 11, color: T.txM }}>{grantedCount}/{groupPerms.length}</span>
                    </div>
                    {!isCollapsed && (
                      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                        <tbody>
                          {groupPerms.map(p => {
                            const granted = currentGrants[p.key] === true;
                            const dirty = !!dirtyGrants && Object.prototype.hasOwnProperty.call(dirtyGrants, p.key);
                            return (
                              <tr key={p.key} style={{ borderBottom: `1px solid ${T.brd}` }}>
                                <td style={{ width: 40, textAlign: "center", padding: "6px 8px" }}>
                                  <input type="checkbox" checked={granted} onChange={() => toggleGrant(p.key)} />
                                </td>
                                <td style={{ padding: "6px 12px" }}>
                                  <div style={{ color: T.tx, fontSize: 12 }}>{p.label}{dirty && <span style={{ color: "#d6a35c", marginLeft: 6 }}>*</span>}</div>
                                  <div style={{ fontSize: 11, color: T.txM }}>{p.description}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create role modal */}
      {showCreate && (
        <CreateRoleModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}

      {/* Edit role modal */}
      {editRole && (
        <EditRoleModal
          role={editRole}
          onClose={() => setEditRole(null)}
          onSaved={async () => { setEditRole(null); await load(); }}
        />
      )}

      {/* Delete role confirmation */}
      {deleteRole && (
        <DeleteRoleModal
          role={deleteRole}
          onClose={() => setDeleteRole(null)}
          onDeleted={async () => { setDeleteRole(null); await load(); }}
        />
      )}
    </div>
  );
};

// ───────────────────── Create modal ─────────────────────

const CreateRoleModal = ({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) => {
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [dashboardVariant, setDashboardVariant] = useState("DashboardView");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const slug = slugify(displayName);
  const submit = async () => {
    if (!displayName.trim() || !slug) { setErr("Display name required"); return; }
    setSaving(true); setErr("");
    const r = await apiCall("/workflows/roles", {
      method: "POST",
      body: JSON.stringify({ slug, display_name: displayName.trim(), description: description.trim() || null, dashboard_variant: dashboardVariant }),
    });
    setSaving(false);
    if (r && r.role) onCreated();
    else setErr((r && r.error) || "Create failed");
  };
  return (
    <Modal onClose={onClose}>
      <div style={{ padding: 20, minWidth: 420 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Create Role</div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Display Name</div>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)} autoFocus
                 style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          <div style={{ fontSize: 10, color: T.txD, fontFamily: "monospace", marginTop: 4 }}>slug: <span style={{ color: T.txM }}>{slug || "\u2014"}</span></div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Description</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                    style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Dashboard Variant</div>
          <select value={dashboardVariant} onChange={e => setDashboardVariant(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit" }}>
            {DASHBOARD_VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {err && <div style={{ color: T.red, fontSize: 11, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn v="ghost" sz="sm" onClick={onClose}>Cancel</Btn>
          <Btn v="primary" sz="sm" onClick={submit} disabled={saving || !slug}>{saving ? "Creating\u2026" : "Create"}</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ───────────────────── Edit modal ─────────────────────

const EditRoleModal = ({ role, onClose, onSaved }: { role: Role; onClose: () => void; onSaved: () => void }) => {
  const [displayName, setDisplayName] = useState(role.display_name);
  const [description, setDescription] = useState(role.description || "");
  const [dashboardVariant, setDashboardVariant] = useState(role.dashboard_variant || "DashboardView");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!displayName.trim()) { setErr("Display name required"); return; }
    setSaving(true); setErr("");
    const r = await apiCall(`/workflows/roles/${role.id}`, {
      method: "PUT",
      body: JSON.stringify({ display_name: displayName.trim(), description: description.trim() || null, dashboard_variant: dashboardVariant }),
    });
    setSaving(false);
    if (r && r.role) onSaved();
    else setErr((r && r.error) || "Save failed");
  };
  return (
    <Modal onClose={onClose}>
      <div style={{ padding: 20, minWidth: 420 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Edit Role</div>
        <div style={{ fontSize: 10, fontFamily: "monospace", color: T.txD, marginBottom: 14 }}>slug: {role.slug}{role.is_system && " (system — slug locked)"}</div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Display Name</div>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                 style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Description</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                    style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical" }} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Dashboard Variant</div>
          <select value={dashboardVariant} onChange={e => setDashboardVariant(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, background: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit" }}>
            {DASHBOARD_VARIANTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        {err && <div style={{ color: T.red, fontSize: 11, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn v="ghost" sz="sm" onClick={onClose}>Cancel</Btn>
          <Btn v="primary" sz="sm" onClick={submit} disabled={saving}>{saving ? "Saving\u2026" : "Save"}</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ───────────────────── Delete confirmation ─────────────────────

const DeleteRoleModal = ({ role, onClose, onDeleted }: { role: Role; onClose: () => void; onDeleted: () => void }) => {
  const userCount = typeof role.user_count === "number" ? role.user_count : 0;
  const blocked = role.is_system || userCount > 0;
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr("");
    const r = await apiCall(`/workflows/roles/${role.id}`, { method: "DELETE" });
    setBusy(false);
    if (r && r.success) onDeleted();
    else setErr((r && r.error) || "Delete failed");
  };
  return (
    <Modal onClose={onClose}>
      <div style={{ padding: 20, minWidth: 380 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Delete role {role.display_name}?</div>
        {role.is_system
          ? <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>System roles cannot be deleted.</div>
          : userCount > 0
            ? <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>This role has {userCount} user{userCount === 1 ? "" : "s"} assigned. Reassign them to another role first.</div>
            : <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>This is a custom role with 0 users. Deleting will soft-delete the row and remove its permissions.</div>}
        {err && <div style={{ color: T.red, fontSize: 11, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn v="ghost" sz="sm" onClick={onClose}>Cancel</Btn>
          <Btn v="primary" sz="sm" onClick={submit} disabled={blocked || busy} style={{ background: T.red, color: "#fff" }}>{busy ? "Deleting\u2026" : "Delete"}</Btn>
        </div>
      </div>
    </Modal>
  );
};
